import {Input, OnInit, inject} from '@angular/core';
import {OnDestroy} from '@angular/core';
import {TemplateRef} from '@angular/core';
import {AfterViewInit} from '@angular/core';
import {ChangeDetectorRef} from '@angular/core';
import {ElementRef} from '@angular/core';
import {ChangeDetectionStrategy} from '@angular/core';
import {Component} from '@angular/core';
import {Observable} from 'rxjs';
import {Subscription} from 'rxjs';
import {fromEvent} from 'rxjs';
import {PaveExplicatifTogglerDirective} from './pave-explicatif-toggler.directive';
import {PaveExplicatifTogglerComponent} from './pave-explicatif-toggler/pave-explicatif-toggler.component';
import { AsyncPipe, NgStyle, NgTemplateOutlet } from "@angular/common";
import { MatIconButton } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatIconModule } from "@angular/material/icon";

/**
 * Composant de pavé explicatif de Pégase à utiliser conjointement avec PaveExplicatifTogglerComponent
 *
 @Input togglerComponent: la référence au composant pegase-pave-explicatif-toggler connecté au pavé
 @Input togglerDirective: la référence de la directive pegasePaveExplicatifToggler placée sur le "bouton" (i.e. élément du DOM) connecté au pavé. (méthode alternative)
 @Input messageString?: le message sous forme de string
 @Input messageTemplate?: la référence au template
 @Input hasClosedButton: si on souhaite afficher une croix de fermeture en haut à droite (default false)
 @Input isClosedByDefault: si on souhaite que le pavé soit fermé par défaut. (default false)
 @Input disable Arrow: pour ne pas afficher de triangle pointant vers le toggler
 @Input customStyles: des styles sous forme clé/valeur à appliquer sur le conteneur. En cas de changement du background-color, donner la même valeur au border-bottom-coor (pour la flèche)

  * utilisation avec string:
  * ```
  <pegase-pave-explicatif-toggler #togglerComponent="toggler"></pegase-pave-explicatif-toggler>

  <pegase-pave-explicatif
  [togglerComponent]="togglerComponent"
  [hasClosedButton]="true"
  [messageString]="'Pour valider votre groupement, vous devez en saisir les descripteurs et y ajouter un ou plusieurs objets de formation'"
  ></pegase-pave-explicatif>
  * ```
  * utilisation avec template:
  * ```
  <pegase-pave-explicatif-toggler #togglerComponent="toggler"></pegase-pave-explicatif-toggler>

  <pegase-pave-explicatif
  [togglerComponent]="togglerComponent"
  [hasClosedButton]="false"
  [messageTemplate]="monTemplate"
  ></pegase-pave-explicatif>
  <ng-template #monTemplate>
  // le contenu de mon template
  </ng-template>
  * ```
 */
@Component({
  selector: 'lib-pegase-pave-explicatif',
  templateUrl: './pave-explicatif.component.html',
  styleUrls: ['./pave-explicatif.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    AsyncPipe,
    NgStyle,
    MatIconButton,
    MatTooltipModule,
    MatIconModule
  ],
  standalone: true
})
export class PaveExplicatifComponent implements AfterViewInit, OnDestroy {

  isOpened = true;
  public leftOffset$!: Observable<number>;
  public clientLeftOffset!: number;
  @Input() togglerDirective!: PaveExplicatifTogglerDirective;
  @Input() togglerComponent!: PaveExplicatifTogglerComponent;
  @Input() disableArrow = false;
  @Input() hasClosedButton = false;

  @Input() set isClosedByDefault(closed: boolean) {
    this.isOpened = !closed
  };

  @Input() messageString?: string;
  @Input() messageTemplate?: TemplateRef<any>;
  @Input() customStyles!: { [propriete: string]: string }
  private subscriptions: Subscription = new Subscription()
  private detector = inject(ChangeDetectorRef);
  private _elementRef = inject(ElementRef);

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.togglerDirective) {
        this.subscriptions.add(
          fromEvent(this.togglerDirective._elementRef.nativeElement, 'click').subscribe(() => {
            this.isOpened = !this.isOpened;
            this.detector.detectChanges();
          })
        )
        this.leftOffset$ = this.togglerDirective.offsetLeft$;
        this.clientLeftOffset = this._elementRef.nativeElement.getBoundingClientRect().left;
        this.detector.detectChanges();
      } else if (this.togglerComponent) {
        this.subscriptions.add(
          fromEvent(this.togglerComponent.directive._elementRef.nativeElement, 'click').subscribe(() => {
            this.isOpened = !this.isOpened;
            this.detector.detectChanges();
          })
        )
        this.leftOffset$ = this.togglerComponent.directive.offsetLeft$;
        this.clientLeftOffset = this._elementRef.nativeElement.getBoundingClientRect().left;
        this.detector.detectChanges();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
